/**
 * Pure adapters for WagePro's leave-balances display and payout-estimate
 * preview (#23311). Mirrors `wagepro_store_leave.ts`'s "raw wire row → UI
 * shape, no React" split so the transforms are unit-testable without
 * rendering.
 *
 * The scaled-integer conventions match docs/money-rules.md and the backend
 * `leave_payout_calculations.ts` engine:
 *
 *   - `balance_days_scaled` — days × 100 (two decimals of precision, kept as
 *     integer on the wire so accrual rounding never lands on a binary float).
 *   - `payout_cents`, `daily_rate_cents`, `total_payout_cents`,
 *     `monthly_salary_cents` — integer minor units, never floats.
 *
 * The three balance-tracked leave types are the exact set the backend pays
 * out on (`BALANCE_TRACKED_LEAVE_TYPES` in
 * `apps/api/src/services/payroll/leave_payout_calculations.ts`): annual,
 * sick, casual. The estimate procedure always returns all three lines in
 * that same stable order (zero-filling missing balances); the balances read
 * does NOT — an employee that never accrued casual leave simply has no row.
 * The adapter here `zero_fill_balances()` normalises the balances read into
 * the same stable three-line shape so the UI can render a fixed table.
 */

/** The three balance-tracked leave types the backend pays out on. */
export const BALANCE_TRACKED_LEAVE_TYPES = [
  'annual',
  'sick',
  'casual',
] as const;

export type BalanceTrackedLeaveType =
  (typeof BALANCE_TRACKED_LEAVE_TYPES)[number];

/**
 * The narrow subset of a `leave_balances` row this UI reads. The backend's
 * `getLeaveBalances` returns the whole Drizzle row (id, entity_id,
 * created_at, updated_at, …) — we only need the type + scaled balance for
 * display, so keeping this narrow means a backend column addition (e.g. a
 * new accrual-period column) doesn't force a wagepro-web wire change.
 */
export interface RealLeaveBalanceRecord {
  leave_type: string;
  balance_days_scaled: number;
}

/** One row of the payout-estimate response, integer minor units throughout. */
export interface RealLeavePayoutEstimateLine {
  leave_type: string;
  balance_days_scaled: number;
  payout_cents: number;
}

/** The `getLeavePayoutEstimate` response shape this UI reads. */
export interface RealLeavePayoutEstimate {
  employee_id: string;
  as_of_date: string;
  monthly_salary_cents: number;
  daily_rate_cents: number;
  lines: RealLeavePayoutEstimateLine[];
  total_payout_cents: number;
}

/** UI-facing balance line — always exactly one per balance-tracked type. */
export interface LeaveBalance {
  leave_type: BalanceTrackedLeaveType;
  balance_days_scaled: number;
  /** `balance_days_scaled / 100`, kept as a number so callers format via the locale. */
  balance_days: number;
}

/** UI-facing payout-estimate line — payout in integer minor units. */
export interface LeavePayoutLine {
  leave_type: BalanceTrackedLeaveType;
  balance_days_scaled: number;
  balance_days: number;
  payout_cents: number;
}

/** UI-facing payout estimate — all monetary values as integer minor units. */
export interface LeavePayoutEstimate {
  employee_id: string;
  as_of_date: string;
  monthly_salary_cents: number;
  daily_rate_cents: number;
  lines: LeavePayoutLine[];
  total_payout_cents: number;
}

const BALANCE_TRACKED_SET: ReadonlySet<string> = new Set(
  BALANCE_TRACKED_LEAVE_TYPES,
);

function isBalanceTracked(raw: string): raw is BalanceTrackedLeaveType {
  return BALANCE_TRACKED_SET.has(raw);
}

/**
 * Canonical i18n-key mapping for the three balance-tracked leave types.
 * Every leave-type render site (employee-profile panel, payout drawer,
 * entity-wide balances tab, opening-balance drawer description) imports
 * this one map so the label resolves from a single source and cannot drift.
 */
export const BALANCE_TRACKED_LEAVE_TYPE_LABEL_KEY: Record<
  BalanceTrackedLeaveType,
  string
> = {
  annual: 'employees.leave_balances.type_annual',
  sick: 'employees.leave_balances.type_sick',
  casual: 'employees.leave_balances.type_casual',
};

/**
 * Returns the i18n key for a leave-type code's display label, or `null`
 * when the code is not balance-tracked (unpaid/other/custom). Callers
 * fall back to the raw code, so a jurisdiction-added type still shows
 * something meaningful without a matching key.
 */
export function leaveTypeLabelKey(code: string): string | null {
  return isBalanceTracked(code)
    ? BALANCE_TRACKED_LEAVE_TYPE_LABEL_KEY[code]
    : null;
}

/**
 * Normalises the `getLeaveBalances` read into a stable three-line shape,
 * zero-filling any missing balance-tracked type so the UI's table can
 * render an "annual / sick / casual" row regardless of what the backend
 * actually returned. Unknown leave types (a `leave_type` column value that
 * isn't one of the three) are dropped — the payout flow only pays out
 * balance-tracked types, so anything else is not display-relevant here.
 * `balance_days_scaled` values below zero are also clamped to zero as a
 * defensive floor — the backend's own accrual engine never emits a
 * negative balance for a payable type, so a negative here would be a bug
 * to surface, not to render as "-1.5 days" against the employee.
 */
export function zero_fill_balances(
  records: readonly RealLeaveBalanceRecord[],
): LeaveBalance[] {
  const by_type = new Map<BalanceTrackedLeaveType, number>();
  for (const row of records) {
    if (!isBalanceTracked(row.leave_type)) continue;
    const scaled = Math.max(0, row.balance_days_scaled);
    by_type.set(row.leave_type, scaled);
  }
  return BALANCE_TRACKED_LEAVE_TYPES.map((type) => {
    const scaled = by_type.get(type) ?? 0;
    return {
      leave_type: type,
      balance_days_scaled: scaled,
      balance_days: scaled / 100,
    };
  });
}

/**
 * Adapts the `getLeavePayoutEstimate` response into the UI shape. Unknown
 * leave-type lines are dropped for the same reason as above; the backend's
 * own estimate always returns lines in `BALANCE_TRACKED_LEAVE_TYPES` order,
 * and this adapter preserves whatever order the backend sent (never
 * re-sorts) so the presented total exactly matches what the mutation will
 * post.
 */
export function derive_payout_estimate(
  raw: RealLeavePayoutEstimate,
): LeavePayoutEstimate {
  const lines: LeavePayoutLine[] = [];
  for (const line of raw.lines) {
    if (!isBalanceTracked(line.leave_type)) continue;
    lines.push({
      leave_type: line.leave_type,
      balance_days_scaled: line.balance_days_scaled,
      balance_days: line.balance_days_scaled / 100,
      payout_cents: line.payout_cents,
    });
  }
  return {
    employee_id: raw.employee_id,
    as_of_date: raw.as_of_date,
    monthly_salary_cents: raw.monthly_salary_cents,
    daily_rate_cents: raw.daily_rate_cents,
    lines,
    total_payout_cents: raw.total_payout_cents,
  };
}
